import React from 'react';
import {
  Document,
  Page,
  View,
  Text,
  Image,
  StyleSheet,
  PDFViewer,
} from '@react-pdf/renderer';
import { EmtConsentPage1, EmtConsentPage2 } from './conscent-emt';
import { KetaminaConsentPage } from './conscent-ketamina';
import { SolicitudPage } from './solicitudVisitaPrint';

const styles = StyleSheet.create({
  page: {
    padding: 24,
    fontSize: 11,
    fontFamily: 'Helvetica',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  headerLeft: {
    flex: 1,
  },
  hospitalName: {
    fontSize: 16,
    fontWeight: 700,
    marginBottom: 2,
  },
  hospitalInfo: {
    fontSize: 10,
    marginBottom: 1,
  },
  logo: {
    width: 90,
    height: 60,
    objectFit: 'contain',
    marginLeft: 8,
  },
  title: {
    textAlign: 'center',
    fontSize: 14,
    fontWeight: 700,
    marginVertical: 8,
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: 700,
    marginTop: 10,
    marginBottom: 6,
  },
  subSectionTitle: {
    fontSize: 10,
    fontWeight: 700,
    marginTop: 6,
    marginBottom: 4,
  },
  box: {
    borderWidth: 1,
    borderColor: '#333',
    borderStyle: 'solid',
    padding: 8,
    borderRadius: 4,
    backgroundColor: '#fff',
  },
  patientGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  field: {
    width: '50%',
    paddingRight: 8,
    marginBottom: 6,
  },
  fieldLabel: {
    fontSize: 9,
    color: '#333',
    marginBottom: 2,
  },
  fieldValue: {
    fontSize: 10,
  },
  historyText: {
    fontSize: 10,
    lineHeight: 1.2,
  },
  // Table styles
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 0.5,
    borderBottomColor: '#ccc',
    paddingVertical: 3,
  },
  tableHeader: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#333',
    paddingVertical: 4,
    backgroundColor: '#f0f0f0',
  },
  tableCell: {
    fontSize: 8,
    paddingHorizontal: 3,
  },
  tableCellBold: {
    fontSize: 8,
    fontWeight: 700,
    paddingHorizontal: 3,
  },
  noData: {
    fontSize: 9,
    color: '#777',
    fontStyle: 'italic',
    padding: 4,
  },
  divider: {
    borderBottomWidth: 0.5,
    borderBottomColor: '#999',
    marginVertical: 4,
  },
  cardBox: {
    borderWidth: 0.5,
    borderColor: '#aaa',
    borderStyle: 'solid',
    padding: 6,
    borderRadius: 3,
    marginBottom: 6,
  },
});

// Parent: expedientes.jsx

export function ClinicalRecordPDF({ data, logo, citas, hojasEnfermeria, visitas, hojasEmt, escalasData }) {
  // data IS the paciente object directly (passed from expedientes.jsx)
  const responsable = data.responsable || {};

  const p = {
    name: data.name || '',
    phone: data.telefono || '',
    age: data.edad || '',
    sex: data.sexo || '',
    birthDate: data.fecha_nacimiento ? new Date(data.fecha_nacimiento).toLocaleDateString() : '',
    responsibleName: responsable.nombre || '',
    responsiblePhone: responsable.telefono || '',
    weightHeight: `${data.peso || ''} / ${data.talla || ''}`,
    occupation: data.ocupacion || '',
    civilStatus: data.estado_civil || '',
    education: data.escolaridad || '',
    birthplace: data.lugar_de_nacimiento || data.ciudad || '',
    folio: data.no_expediente || '',
  };

  const safeCitas = Array.isArray(citas) ? citas : [];
  const safeHojas = Array.isArray(hojasEnfermeria) ? hojasEnfermeria : [];
  const safeVisitas = Array.isArray(visitas) ? visitas : [];
  const safeHojasEmt = Array.isArray(hojasEmt) ? hojasEmt : [];
  const safeEscalas = Array.isArray(escalasData) ? escalasData : [];

  const formatDate = (d) => {
    if (!d) return '';
    try { return new Date(d).toLocaleDateString('es-MX'); } catch { return ''; }
  };
  const formatTime = (d) => {
    if (!d) return '';
    try { return new Date(d).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' }); } catch { return ''; }
  };

  return (
    <Document>
      {/* PAGE 1: Patient Data + Citas */}
      <Page size="LETTER" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            <Text style={{ fontSize: 14, fontWeight: 700, marginTop: 6 }}>Folio: {p.folio || '____________________'}</Text>
          </View>
          {logo ? (
            <Image src={logo} style={styles.logo} />
          ) : (
            <View style={{ width: 90, height: 60 }} />
          )}
        </View>

        <Text style={styles.title}>Expediente clinico</Text>

        {/* Patient data */}
        <Text style={styles.sectionTitle}>Datos del paciente</Text>
        <View style={[styles.box, { marginBottom: 8 }]}>
          <View style={styles.patientGrid}>
            <View style={styles.field}>
              <Text style={styles.fieldLabel}>Nombre</Text>
              <Text style={styles.fieldValue}>{p.name}</Text>
            </View>
            <View style={styles.field}>
              <Text style={styles.fieldLabel}>Telefono</Text>
              <Text style={styles.fieldValue}>{p.phone}</Text>
            </View>
            <View style={styles.field}>
              <Text style={styles.fieldLabel}>Edad</Text>
              <Text style={styles.fieldValue}>{p.age}</Text>
            </View>
            <View style={styles.field}>
              <Text style={styles.fieldLabel}>Sexo</Text>
              <Text style={styles.fieldValue}>{p.sex}</Text>
            </View>
            <View style={styles.field}>
              <Text style={styles.fieldLabel}>Fecha de nacimiento</Text>
              <Text style={styles.fieldValue}>{p.birthDate}</Text>
            </View>
            <View style={styles.field}>
              <Text style={styles.fieldLabel}>Nombre del responsable</Text>
              <Text style={styles.fieldValue}>{p.responsibleName}</Text>
            </View>
            <View style={styles.field}>
              <Text style={styles.fieldLabel}>Telefono del responsable</Text>
              <Text style={styles.fieldValue}>{p.responsiblePhone}</Text>
            </View>
            <View style={styles.field}>
              <Text style={styles.fieldLabel}>Peso (kg) / Talla (cm)</Text>
              <Text style={styles.fieldValue}>{p.weightHeight}</Text>
            </View>
            <View style={styles.field}>
              <Text style={styles.fieldLabel}>Ocupacion</Text>
              <Text style={styles.fieldValue}>{p.occupation}</Text>
            </View>
            <View style={styles.field}>
              <Text style={styles.fieldLabel}>Estado civil</Text>
              <Text style={styles.fieldValue}>{p.civilStatus}</Text>
            </View>
            <View style={styles.field}>
              <Text style={styles.fieldLabel}>Escolaridad</Text>
              <Text style={styles.fieldValue}>{p.education}</Text>
            </View>
            <View style={styles.field}>
              <Text style={styles.fieldLabel}>Lugar de nacimiento</Text>
              <Text style={styles.fieldValue}>{p.birthplace}</Text>
            </View>
            <View style={styles.field}>
              <Text style={styles.fieldLabel}>Folio</Text>
              <Text style={styles.fieldValue}>{p.folio || '____________________'}</Text>
            </View>
          </View>
        </View>

        {/* Citas */}
        <Text style={styles.sectionTitle}>Citas</Text>
        <View style={styles.box}>
          {safeCitas.length > 0 ? (
            <>
              <View style={styles.tableHeader}>
                <Text style={[styles.tableCellBold, { width: '25%' }]}>Fecha y hora</Text>
                <Text style={[styles.tableCellBold, { width: '20%' }]}>Medico</Text>
                <Text style={[styles.tableCellBold, { width: '20%' }]}>Servicio</Text>
                <Text style={[styles.tableCellBold, { width: '35%' }]}>Comentarios</Text>
              </View>
              {safeCitas.map((cita, i) => (
                <View style={styles.tableRow} key={i}>
                  <Text style={[styles.tableCell, { width: '25%' }]}>{cita.fecha_hora || formatDate(cita.createdAt)}</Text>
                  <Text style={[styles.tableCell, { width: '20%' }]}>{cita.medico?.name || ''}</Text>
                  <Text style={[styles.tableCell, { width: '20%' }]}>{cita.servicio || ''}</Text>
                  <Text style={[styles.tableCell, { width: '35%' }]}>{cita.comentarios || ''}</Text>
                </View>
              ))}
            </>
          ) : (
            <Text style={styles.noData}>Sin citas registradas</Text>
          )}
        </View>
      </Page>

      {/* PAGE 2: Hojas de Enfermeria */}
      <Page size="LETTER" style={styles.page}>
        <Text style={styles.sectionTitle}>Hojas de Enfermeria</Text>
        {safeHojas.length > 0 ? safeHojas.map((hoja, i) => (
          <View style={styles.cardBox} key={i}>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 }}>
              <Text style={[styles.tableCellBold, { fontSize: 10 }]}>Aplicacion {i + 1}</Text>
              <Text style={styles.tableCell}>{formatDate(hoja.createdAt)}</Text>
            </View>
            <View style={{ flexDirection: 'row', gap: 12, marginBottom: 4 }}>
              <Text style={styles.tableCell}>Peso: {hoja.peso || ''} kg</Text>
              {hoja.imc && <Text style={styles.tableCell}>IMC: {hoja.imc}</Text>}
              <Text style={styles.tableCell}>Dosis: {hoja.dosis || ''}</Text>
              {hoja.medico_tratante && <Text style={styles.tableCell}>Medico: {hoja.medico_tratante}</Text>}
            </View>
            {(hoja.hora_inicio || hoja.hora_termino) && (
              <View style={{ flexDirection: 'row', gap: 12, marginBottom: 4 }}>
                {hoja.hora_inicio && <Text style={styles.tableCell}>Hora inicio: {formatTime(hoja.hora_inicio)}</Text>}
                {hoja.hora_termino && <Text style={styles.tableCell}>Hora termino: {formatTime(hoja.hora_termino)}</Text>}
              </View>
            )}

            {/* Vitals table */}
            <View style={styles.tableHeader}>
              <Text style={[styles.tableCellBold, { width: '14%' }]}></Text>
              <Text style={[styles.tableCellBold, { width: '14%' }]}>Hora</Text>
              <Text style={[styles.tableCellBold, { width: '14%' }]}>Temp.</Text>
              <Text style={[styles.tableCellBold, { width: '15%' }]}>F. Cardiaca</Text>
              <Text style={[styles.tableCellBold, { width: '15%' }]}>F. Resp.</Text>
              <Text style={[styles.tableCellBold, { width: '14%' }]}>T/A</Text>
              <Text style={[styles.tableCellBold, { width: '14%' }]}>Sat O2</Text>
            </View>
            {['inicio', 'intermedio', 'termino'].map((fase) => {
              const d = hoja[fase];
              if (!d) return null;
              return (
                <View style={styles.tableRow} key={fase}>
                  <Text style={[styles.tableCellBold, { width: '14%' }]}>{fase.charAt(0).toUpperCase() + fase.slice(1)}</Text>
                  <Text style={[styles.tableCell, { width: '14%' }]}>{formatTime(d.hora)}</Text>
                  <Text style={[styles.tableCell, { width: '14%' }]}>{d.temperatura || ''}</Text>
                  <Text style={[styles.tableCell, { width: '15%' }]}>{d.frecuencia_cardiaca || ''}</Text>
                  <Text style={[styles.tableCell, { width: '15%' }]}>{d.frecuencia_respiratoria || ''}</Text>
                  <Text style={[styles.tableCell, { width: '14%' }]}>{d.presion_arterial || ''}</Text>
                  <Text style={[styles.tableCell, { width: '14%' }]}>{d.saturacion_oxigeno || ''}</Text>
                </View>
              );
            })}

            {/* Observaciones and scales */}
            {hoja.observaciones && (
              <View style={{ marginTop: 4 }}>
                <Text style={styles.tableCellBold}>Observaciones:</Text>
                <Text style={styles.tableCell}>{hoja.observaciones}</Text>
              </View>
            )}
            <View style={{ flexDirection: 'row', gap: 12, marginTop: 2 }}>
              {hoja.phq9 && <Text style={styles.tableCell}>PHQ-9: {hoja.phq9}</Text>}
              {hoja.gad7 && <Text style={styles.tableCell}>GAD-7: {hoja.gad7}</Text>}
              {hoja.columbia && <Text style={styles.tableCell}>Columbia: {hoja.columbia}</Text>}
            </View>
          </View>
        )) : (
          <View style={styles.box}>
            <Text style={styles.noData}>Sin hojas de enfermeria registradas</Text>
          </View>
        )}
      </Page>

      {/* PAGE 3: Hojas de Registro EMT */}
      <Page size="LETTER" style={styles.page}>
        <Text style={styles.sectionTitle}>Hojas de Registro EMT</Text>
        {safeHojasEmt.length > 0 ? safeHojasEmt.map((hojaEmt, i) => (
          <View style={styles.cardBox} key={i}>
            <Text style={[styles.tableCellBold, { fontSize: 10, marginBottom: 4 }]}>
              Registro EMT - {formatDate(hojaEmt.createdAt)}
            </Text>

            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 4 }}>
              <Text style={styles.tableCell}>Peso: {hojaEmt.peso || ''} kg</Text>
              <Text style={styles.tableCell}>Dosis: {hojaEmt.dosis || ''}</Text>
              {hojaEmt.nasion_inion && <Text style={styles.tableCell}>Nasion-Inion: {hojaEmt.nasion_inion} cm</Text>}
              {hojaEmt.auricular_izq_auricular_der && <Text style={styles.tableCell}>Auricular Izq-Der: {hojaEmt.auricular_izq_auricular_der} cm</Text>}
              {hojaEmt.circunferencia && <Text style={styles.tableCell}>Circunferencia: {hojaEmt.circunferencia}</Text>}
              {hojaEmt.gorro_a_nasion && <Text style={styles.tableCell}>Gorro a nasion: {hojaEmt.gorro_a_nasion} cm</Text>}
            </View>

            {/* Farmacos table */}
            <Text style={styles.subSectionTitle}>Farmacos</Text>
            <View style={styles.tableHeader}>
              <Text style={[styles.tableCellBold, { width: '16%' }]}></Text>
              <Text style={[styles.tableCellBold, { width: '21%' }]}>Farmaco</Text>
              <Text style={[styles.tableCellBold, { width: '16%' }]}>Manana</Text>
              <Text style={[styles.tableCellBold, { width: '16%' }]}>Tarde</Text>
              <Text style={[styles.tableCellBold, { width: '16%' }]}>Noche</Text>
              <Text style={[styles.tableCellBold, { width: '15%' }]}>Tiempo tx</Text>
            </View>
            {['primero', 'segundo', 'tercero', 'cuarto', 'quinto'].map((orden) => {
              const f = hojaEmt[orden];
              if (!f || !f.farmaco) return null;
              return (
                <View style={styles.tableRow} key={orden}>
                  <Text style={[styles.tableCellBold, { width: '16%' }]}>{orden.charAt(0).toUpperCase() + orden.slice(1)}</Text>
                  <Text style={[styles.tableCell, { width: '21%' }]}>{f.farmaco || ''}</Text>
                  <Text style={[styles.tableCell, { width: '16%' }]}>{f.mañana || ''}</Text>
                  <Text style={[styles.tableCell, { width: '16%' }]}>{f.tarde || ''}</Text>
                  <Text style={[styles.tableCell, { width: '16%' }]}>{f.noche || ''}</Text>
                  <Text style={[styles.tableCell, { width: '15%' }]}>{f.tiempo_tx || ''}</Text>
                </View>
              );
            })}

            {/* Protocol evaluations */}
            {(hojaEmt.row1?.protocolo || hojaEmt.row2?.protocolo || hojaEmt.row3?.protocolo) && (
              <View style={{ marginTop: 6 }}>
                <Text style={styles.subSectionTitle}>Protocolos / Evaluaciones</Text>
                <View style={styles.tableHeader}>
                  <Text style={[styles.tableCellBold, { width: '20%' }]}>Protocolo</Text>
                  <Text style={[styles.tableCellBold, { width: '20%' }]}>Eval. 1</Text>
                  <Text style={[styles.tableCellBold, { width: '20%' }]}>Eval. 2</Text>
                  <Text style={[styles.tableCellBold, { width: '20%' }]}>Eval. 3</Text>
                  <Text style={[styles.tableCellBold, { width: '20%' }]}>Eval. 4</Text>
                </View>
                {['row1', 'row2', 'row3'].map((rowKey) => {
                  const row = hojaEmt[rowKey];
                  if (!row || !row.protocolo) return null;
                  return (
                    <View style={styles.tableRow} key={rowKey}>
                      <Text style={[styles.tableCell, { width: '20%' }]}>{row.protocolo}</Text>
                      <Text style={[styles.tableCell, { width: '20%' }]}>{row.evaluacion_1 || ''}</Text>
                      <Text style={[styles.tableCell, { width: '20%' }]}>{row.evaluacion_2 || ''}</Text>
                      <Text style={[styles.tableCell, { width: '20%' }]}>{row.evaluacion_3 || ''}</Text>
                      <Text style={[styles.tableCell, { width: '20%' }]}>{row.evaluacion_4 || ''}</Text>
                    </View>
                  );
                })}
              </View>
            )}

            {hojaEmt.observaciones && (
              <View style={{ marginTop: 4 }}>
                <Text style={styles.tableCellBold}>Observaciones:</Text>
                <Text style={styles.tableCell}>{hojaEmt.observaciones}</Text>
              </View>
            )}
          </View>
        )) : (
          <View style={styles.box}>
            <Text style={styles.noData}>Sin hojas de registro EMT</Text>
          </View>
        )}

        {/* Escalas EMT */}
        <Text style={styles.sectionTitle}>Escalas</Text>
        {safeEscalas.length > 0 ? (
          <View style={styles.box}>
            <View style={styles.tableHeader}>
              <Text style={[styles.tableCellBold, { width: '20%' }]}>Sesion</Text>
              <Text style={[styles.tableCellBold, { width: '25%' }]}>Tipo</Text>
              <Text style={[styles.tableCellBold, { width: '25%' }]}>Protocolo</Text>
              <Text style={[styles.tableCellBold, { width: '30%' }]}>Resultado</Text>
            </View>
            {safeEscalas.map((escala, i) => (
              <View style={styles.tableRow} key={i}>
                <Text style={[styles.tableCell, { width: '20%' }]}>{escala.session || ''}</Text>
                <Text style={[styles.tableCell, { width: '25%' }]}>{escala.tipo || ''}</Text>
                <Text style={[styles.tableCell, { width: '25%' }]}>{escala.protocolo || ''}</Text>
                <Text style={[styles.tableCell, { width: '30%' }]}>{escala.resultado || ''}</Text>
              </View>
            ))}
          </View>
        ) : (
          <View style={styles.box}>
            <Text style={styles.noData}>Sin escalas registradas</Text>
          </View>
        )}
      </Page>

      {/* Consentimiento EMT — page 1 */}
      <EmtConsentPage1 pacienteData={data} />

      {/* Consentimiento EMT — page 2 */}
      <EmtConsentPage2 />

      {/* Consentimiento Ketamina */}
      <KetaminaConsentPage pacienteData={data} />

      {/* Solicitudes de tratamiento — one page per visita */}
      {safeVisitas.map((visita, i) => (
        <SolicitudPage
          key={i}
          pacienteData={data}
          visitaData={visita}
          tipo={visita.tipo_visita}
        />
      ))}
    </Document>
  );
}


export default function ExpedienteForPrintNext({
  data = {},
  logo = "https://app.recreamed.com/images/noimg.jpg",
  historyText,
  citas = [],
  hojasEnfermeria = [],
  visitas = [],
  hojasEmt = [],
  escalasData = [],
}) {
  return (
    <PDFViewer style={{ width: '100%', height: '90vh' }}>
      <ClinicalRecordPDF
        data={data}
        logo={logo}
        citas={citas}
        hojasEnfermeria={hojasEnfermeria}
        visitas={visitas}
        hojasEmt={hojasEmt}
        escalasData={escalasData}
      />
    </PDFViewer>
  )
}
